import { Injectable, inject } from '@angular/core';
import type { Visualize3dComponent } from '../v3d-components/visualize3d-component';
import { transformComponentCreator } from '../v3d-components/transform-component';
import { QModelComponent, qmodelComponentCreator } from '../v3d-components/q-model-component';
import { interactiveComponentCreator } from '../v3d-components/interactive-component';
import { threeJsMaterialModifierComponentCreator } from '../v3d-components/three-js-material-modifier-component';
import { Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { map } from 'rxjs/operators';
import { ambientLightComponentCreator } from '../v3d-components/ambient-light-component';
import { spotLightComponentCreator } from '../v3d-components/spot-light-component';
import type { Visualize3dComponentType } from '../v3d-components/visualize3d-component-type';

@Injectable({
  providedIn: 'root',
})
export class Visualize3dComponentFactoryService {
  private apiService = inject(ApiService);

  private componentConstructors = new Map<string, Observable<Visualize3dComponent>>();

  constructor() {
    this.componentConstructors.set('Transform', of(transformComponentCreator.createComponent()));
    this.componentConstructors.set('QModel', this.createQModelWithValidId());
    this.componentConstructors.set('Interactive', of(interactiveComponentCreator.createComponent()));
    this.componentConstructors.set('ThreeJsMaterialModifier', of(threeJsMaterialModifierComponentCreator.createComponent()));
    this.componentConstructors.set('AmbientLight', of(ambientLightComponentCreator.createComponent()));
    this.componentConstructors.set('SpotLight', of(spotLightComponentCreator.createComponent()));
  }

  public getAvailableComponentTypes(): string[] {
    return Array.from(this.componentConstructors.keys());
  }

  public create<T extends Visualize3dComponent>(componentType: Visualize3dComponentType): Observable<T> {
    if (!this.isValidComponent(componentType)) {
      throw new Error(`Component type ${componentType} is not a valid type`);
    }
    return this.componentConstructors.get(componentType) as Observable<T>;
  }

  private isValidComponent(componentType: Visualize3dComponentType): boolean {
    return this.componentConstructors.has(componentType);
  }

  // TODO: Should be refactored to elsewhere e.g QModelFactory. Component specific creation logic do not belong in this factory.
  private createQModelWithValidId(): Observable<QModelComponent> {
    return this.apiService.getQModelIds().pipe(
      map(ids => {
        const qModel = qmodelComponentCreator.createComponent();
        qModel.id = ids[0];
        return qModel;
      }),
    );
  }
}
